import React, { useMemo } from "react";
import { createPortal } from "react-dom";
import { ComponentRenderer } from "./ComponentRenderer";
import { useAppDispatch, useAppSelector } from "../state/hooks";
import { resetScreenFormData, updateScreenFormField } from "../state/slices/formsSlice";
import { closeProfileEditDrawer } from "../state/slices/profileEditDrawerSlice";
import {
  buildProfileEditDrawerConfig,
  PROFILE_EDIT_DRAWER_SCREEN_ID,
} from "../utils/hrmsProfileEdit";

interface ProfileEditDrawerProps {
  onActionTrigger: (actionId: string, data?: any) => void;
}

/**
 * Slide-over drawer for editing a single employee profile section (screen 2217).
 */
export const ProfileEditDrawer: React.FC<ProfileEditDrawerProps> = ({
  onActionTrigger,
}) => {
  const dispatch = useAppDispatch();
  const { isOpen, title, saveActionId, fields } = useAppSelector(
    (s) => s.profileEditDrawer
  );
  const formData = useAppSelector(
    (s) => s.forms.formDataByScreen[PROFILE_EDIT_DRAWER_SCREEN_ID] || {}
  );
  const isSaving = useAppSelector(
    (s) => s.forms.savingByScreen[PROFILE_EDIT_DRAWER_SCREEN_ID] || false
  );

  const drawerConfig = useMemo(() => {
    if (!fields.length || !saveActionId) return [];
    return buildProfileEditDrawerConfig(fields, saveActionId);
  }, [fields, saveActionId]);

  const handleClose = () => {
    dispatch(closeProfileEditDrawer());
    dispatch(resetScreenFormData(PROFILE_EDIT_DRAWER_SCREEN_ID));
  };

  if (!isOpen) return null;

  const body = (
    <>
      <div
        className="fixed inset-0 z-[12000] bg-black/40"
        aria-hidden="true"
        onClick={handleClose}
      />
      <MotionlessPanel title={title} onClose={handleClose}>
        {drawerConfig.length > 0 ? (
          <ComponentRenderer
            config={drawerConfig}
            formData={formData}
            screenContextId={PROFILE_EDIT_DRAWER_SCREEN_ID}
            onFormDataChange={(name, value) =>
              dispatch(
                updateScreenFormField({
                  screenId: PROFILE_EDIT_DRAWER_SCREEN_ID,
                  name,
                  value,
                })
              )
            }
            onActionTrigger={onActionTrigger}
          />
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No fields configured for this section.
          </p>
        )}
        {isSaving && (
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Saving…</p>
        )}
      </MotionlessPanel>
    </>
  );

  if (typeof document === "undefined") return null;
  return createPortal(body, document.body);
};

function MotionlessPanel({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed top-0 right-0 z-[12001] h-full w-full max-w-xl bg-white dark:bg-gray-900 shadow-2xl flex flex-col border-l border-gray-200 dark:border-gray-700"
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-edit-drawer-title"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <h2
          id="profile-edit-drawer-title"
          className="text-lg font-semibold text-gray-900 dark:text-gray-100"
        >
          {title}
        </h2>
        <button
          type="button"
          className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 text-2xl leading-none px-2"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4">{children}</div>
    </div>
  );
}

export default ProfileEditDrawer;
